import React from 'react';
import { usePermissions } from 'react-admin';
import { httpClient } from '../utils/dataProvider';
import { API_BASE_URL } from '../utils/config';
import { hasAccess } from '../utils/permissions';
import { PageShell, PageHeader, Surface, Row, RowText, RowMeta, StatusPill, ActionButton, StatePanel } from '../components/ui';

// Display order, labels and descriptions mirror META_FEATURE_FLAG_KEYS in
// skilzBackend app/models/metaFeatureFlag.js (the source of truth for keys).
export const META_FLAGS = [
  { key: 'metaIgPortfolioImport', label: 'Instagram portfolio import', description: 'Importing Instagram media into a provider portfolio.' },
  { key: 'metaIgThumbnailPreview', label: 'Instagram thumbnail previews', description: 'Live, never-stored IG thumbnail URLs for GET /api/instagramMedia/me/previews. Requires Instagram portfolio import to also be on; on its own it does nothing.', requires: 'metaIgPortfolioImport' },
  { key: 'metaIgFeedImport', label: 'Instagram feed import', description: 'Publishing picked Instagram posts into a skill as public Skilz feed posts (POST /api/instagramMedia/me/import-to-feed). Requires Instagram portfolio import to also be on; on its own it does nothing.', requires: 'metaIgPortfolioImport' },
  { key: 'metaSocialTrustBadge', label: 'Social trust badge', description: 'Provider trust badge derived from a linked social account.' },
  { key: 'metaFacebookLink', label: 'Facebook account linking', description: 'Linking a Facebook account to a Skilz account.' },
  { key: 'metaFriendsOnSkilz', label: 'Friends on Skilz', description: "Surfacing a user's Facebook friends who are on Skilz." },
  { key: 'metaAppEvents', label: 'Meta app events', description: 'Sending app events to Meta.' },
];

// Backend propagation: flag values are memoized per process for MEMO_TTL_MS.
const PROPAGATION_SECONDS = 30;

// Backend error payload shape: { errors: { msg: 'CODE' } }. Surface the code,
// falling back to the HTTP error message, never a generic string.
const errorText = err => (err && err.body && err.body.errors && err.body.errors.msg)
  || (err && err.message)
  || (err && err.status ? `HTTP ${err.status}` : 'UNKNOWN_ERROR');

export default function MetaFlags() {
  const { permissions, loaded } = usePermissions();
  const roles = (permissions && permissions.adminRoles) || [];
  const rolesKey = roles.slice().sort().join(',');
  const canView = hasAccess('meta-flags', roles);
  // Write access is SUPER_ADMIN only. Deliberately not hasAccess(): that returns
  // true for every role listed in PERMISSION_MAP (OPERATIONS included).
  const canWrite = roles.includes('SUPER_ADMIN');

  const [flags, setFlags] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState('');
  const [busy, setBusy] = React.useState({});
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const requestSeq = React.useRef(0);

  const loadFlags = React.useCallback(async () => {
    const seq = ++requestSeq.current;
    setLoading(true); setLoadError('');
    try {
      const { json } = await httpClient(`${API_BASE_URL}/admin/meta-flags`);
      if (seq === requestSeq.current) setFlags((json && json.flags) || {});
    } catch (err) {
      if (seq === requestSeq.current) setLoadError(errorText(err));
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, []);

  React.useEffect(() => { if (loaded && canView) loadFlags(); }, [loaded, rolesKey, loadFlags]);

  const toggle = async flag => {
    if (!canWrite || busy[flag.key] || !flags) return;
    const next = flags[flag.key] !== true;
    const prompt = next
      ? `Turn ON "${flag.label}" (${flag.key})?\n\nThis changes production behaviour immediately for all users (allow up to ~${PROPAGATION_SECONDS}s to reach every server).`
      : `Turn OFF "${flag.label}" (${flag.key})?\n\nThis kill switch changes production behaviour immediately (allow up to ~${PROPAGATION_SECONDS}s to reach every server).`;
    if (!window.confirm(prompt)) return;
    setBusy(current => ({ ...current, [flag.key]: true }));
    setError(''); setMessage('');
    try {
      await httpClient(`${API_BASE_URL}/admin/meta-flags/${encodeURIComponent(flag.key)}`, {
        method: 'PATCH',
        body: JSON.stringify({ enabled: next }),
      });
      setMessage(`${flag.label} turned ${next ? 'ON' : 'OFF'}. Allow up to ~${PROPAGATION_SECONDS}s to take effect everywhere.`);
      // Show server truth, not our assumption of it.
      await loadFlags();
    } catch (err) {
      setError(`Could not update ${flag.label}: ${errorText(err)}`);
    } finally {
      setBusy(current => { const copy = { ...current }; delete copy[flag.key]; return copy; });
    }
  };

  if (!loaded) return <PageShell><StatePanel state="loading">Loading permissions…</StatePanel></PageShell>;
  if (!canView) return <PageShell><StatePanel state="error">You do not have permission to view this page.</StatePanel></PageShell>;

  const parentOff = flags && flags.metaIgPortfolioImport !== true;

  // Render the server's keys, not just the ones known at build time: a flag
  // added in the backend must never be invisible/untoggleable here. Known keys
  // keep their curated order, labels and descriptions; unknown keys are
  // appended with their raw key as the label.
  const rows = flags
    ? [
      ...META_FLAGS.filter(flag => Object.prototype.hasOwnProperty.call(flags, flag.key)),
      ...Object.keys(flags)
        .filter(key => !META_FLAGS.some(flag => flag.key === key))
        .sort()
        .map(key => ({ key, label: key, description: 'Not described in this build of the admin panel. Added in the backend after this page shipped.' })),
    ]
    : [];

  return <PageShell>
    <PageHeader eyebrow="System" title="Meta Feature Flags" description="Kill switches for the Facebook & Instagram integration. Off means the feature is inert in production." />
    <StatePanel state="empty">Changes can take up to ~{PROPAGATION_SECONDS} seconds to take effect everywhere: each server caches flag values for {PROPAGATION_SECONDS}s.</StatePanel>
    {!canWrite && <StatePanel state="empty">Read-only: only a Super Admin can change these flags.</StatePanel>}
    {error && <StatePanel state="error">{error}</StatePanel>}
    {message && <StatePanel state="empty">{message}</StatePanel>}
    <Surface title="Kill switches" actions={<ActionButton variant="secondary" type="button" onClick={loadFlags} disabled={loading}>Refresh</ActionButton>}>
      {loadError && <StatePanel state="error">Unable to load Meta flags: {loadError}</StatePanel>}
      {loading && !flags && <StatePanel state="loading">Loading Meta flags…</StatePanel>}
      {flags && rows.map(flag => {
        const on = flags[flag.key] === true;
        const rowBusy = Boolean(busy[flag.key]);
        const inert = Boolean(flag.requires) && on && parentOff;
        return <Row key={flag.key} right={<>
          {rowBusy && <span className="sk-muted" role="status">Saving…</span>}
          <ActionButton
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={flag.label}
            variant={on ? 'primary' : 'secondary'}
            disabled={!canWrite || rowBusy}
            aria-busy={rowBusy || undefined}
            onClick={() => toggle(flag)}
          >{on ? 'On' : 'Off'}</ActionButton>
        </>}>
          <RowText>
            <strong className="sk-title">{flag.label}</strong> <StatusPill status={on ? 'active' : 'deactivated'}>{on ? 'On' : 'Off'}</StatusPill>
            <RowMeta>{flag.description}</RowMeta>
            <RowMeta dim><code>{flag.key}</code></RowMeta>
            {inert && <p style={{ color: 'var(--sk-warning)', fontWeight: 700, margin: '6px 0 0' }} role="alert" data-testid="inert-warning">Inert: this flag is ON but Instagram portfolio import is OFF, so it does nothing until that parent switch is turned on.</p>}
          </RowText>
        </Row>;
      })}
    </Surface>
  </PageShell>;
}
